import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender, Row } from '@tanstack/react-table';
import cn from 'classnames';

import { isEmpty } from '@utils/common';

import { DRAG_HANDLE_COLUMN_ID } from 'components/common/Table/constants';

import DragHandle from '../DragHandle';

import styles from './SortableRow.module.scss';

interface ISortableRowProps {
	row: Row<any>;
	index: number;
	isPinnedColumns: boolean;
	stickyLeftColumnIndexes?: number[];
	isWithoutActions: boolean;
	isDragDisabled?: boolean;
	dragHandle?: boolean;
}

const SortableRow: React.FC<ISortableRowProps> = ({
	row,
	index,
	isPinnedColumns,
	stickyLeftColumnIndexes,
	isWithoutActions,
	isDragDisabled = false,
	dragHandle = true,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: row.id,
		disabled: isDragDisabled,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const cells = row.getVisibleCells();
	const stickyCells = cells.filter((_, k) => stickyLeftColumnIndexes?.includes(k));
	const notStickyCells = isPinnedColumns
		? cells.filter((_, k) => !stickyLeftColumnIndexes?.includes(k))
		: cells;

	const isSubRow = !!row.depth;

	const rowClassNames = cn({
		[styles.dragging]: isDragging,
		[styles.dragDisabled]: isDragDisabled,
	});

	return (
		<tr
			key={`tbody-tr-${index}`}
			ref={setNodeRef}
			className={rowClassNames}
			data-testid={`tbody-tr-${index}`}
			style={style}
			{...(dragHandle ? {} : { ...attributes, ...listeners })}
		>
			{!isEmpty(stickyCells) && isPinnedColumns
				&& stickyCells.map((cell, j) => {
					const isDragHandleColumn = cell.column.id === DRAG_HANDLE_COLUMN_ID;

					return (
						<td
							key={`tbody-td-${j}`}
							className={cn('table-cell', styles.stickyCellLeft, {
								[styles.dragHandleCell]: isDragHandleColumn,
							})}
						>
							{isDragHandleColumn ? (
								<DragHandle
									disabled={isDragDisabled}
									id={row.id}
									isDragging={isDragging}
									{...attributes}
									{...listeners}
								/>
							) : (
								flexRender(cell.column.columnDef.cell, cell.getContext())
							)}
						</td>
					);
				})
			}

			{notStickyCells.map((cell, j, elements: string | any[]) => {
				const isDragHandleColumn = cell.column.id === DRAG_HANDLE_COLUMN_ID;

				return (
					<td
						key={`tbody-td-${j}`}
						className={cn('table-cell', {
							[styles.stickyCellRight]: !isWithoutActions
								&& isPinnedColumns
								&& (elements.length - 1 === j),
							[styles.expandedRow]: isSubRow,
							[styles.dragHandleCell]: isDragHandleColumn,
							[styles.stickyCellLeft]: isDragHandleColumn,
						})}
					>
						{isDragHandleColumn ? (
							<DragHandle
								disabled={isDragDisabled}
								id={row.id}
								isDragging={isDragging}
								{...attributes}
								{...listeners}
							/>
						) : (
							flexRender(cell.column.columnDef.cell, cell.getContext())
						)}
					</td>
				);
			})}
		</tr>
	);
};

export default SortableRow;
